import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Gift, Copy, ArrowRight, Trophy } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

export default function Rewards() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [email, setEmail] = useState(searchParams.get("email") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [redeeming, setRedeeming] = useState("");

    const load = async (e) => {
        if (!e || !/\S+@\S+\.\S+/.test(e)) return;
        setLoading(true);
        try {
            const r = await api.get(`/loyalty/${encodeURIComponent(e.trim().toLowerCase())}`);
            setData(r.data);
        } catch {
            toast.error("Couldn't load your rewards. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (email) load(email);
    }, []);

    const submit = (evt) => {
        evt.preventDefault();
        setSearchParams({ email });
        load(email);
    };

    const redeem = async (reward) => {
        if (!data) return;
        if (data.balance < reward.points) {
            toast.error(`You need ${reward.points - data.balance} more points.`);
            return;
        }
        if (!window.confirm(`Redeem ${reward.points} points for "${reward.label}"?`)) return;
        setRedeeming(reward.key);
        try {
            const r = await api.post(`/loyalty/${encodeURIComponent(email.trim().toLowerCase())}/redeem`, { reward_key: reward.key });
            toast.success(`Reward unlocked. Voucher: ${r.data.voucher.code}`);
            await load(email);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't redeem — try again.");
        } finally {
            setRedeeming("");
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Voucher code copied.");
        } catch {
            toast.error("Couldn't copy — long-press to select.");
        }
    };

    return (
        <div data-testid="rewards-page" className="min-h-screen bg-cream">
            <Navbar variant="solid" />
            <section className="pt-32 pb-24 px-6 md:px-12">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Loyalty Rewards</span>
                        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 tracking-tight">
                            Every clean earns <span className="italic text-gold-dark">points</span>.
                        </h1>
                        <p className="font-outfit text-base text-muted-foreground mt-4 max-w-xl mx-auto">
                            Earn 1 point per €1 spent. Points vest when your booking is marked completed by our team.
                        </p>
                    </div>

                    {/* Email lookup */}
                    <form onSubmit={submit} className="max-w-xl mx-auto flex gap-2 mb-10" data-testid="rewards-lookup">
                        <input
                            data-testid="rewards-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            className="flex-1 border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
                        />
                        <button
                            type="submit"
                            data-testid="rewards-submit"
                            disabled={loading}
                            className="bg-navy text-gold border border-gold px-6 py-3 font-outfit text-sm uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
                        >
                            {loading ? "Loading…" : "View my points"}
                        </button>
                    </form>

                    {data && (
                        <>
                            {/* Balance card */}
                            <div className="bg-navy text-cream border border-gold/40 p-8 md:p-10 grid sm:grid-cols-3 gap-6 mb-10" data-testid="rewards-balance">
                                <div>
                                    <div className="uppercase tracking-[0.2em] text-[10px] text-gold font-semibold mb-2">Available</div>
                                    <div className="font-playfair text-4xl md:text-5xl text-gold" data-testid="balance-value">{data.balance}</div>
                                    <div className="text-xs text-cream/60 mt-1 font-outfit">points to redeem</div>
                                </div>
                                <div>
                                    <div className="uppercase tracking-[0.2em] text-[10px] text-gold font-semibold mb-2">Pending</div>
                                    <div className="font-playfair text-3xl md:text-4xl text-cream">{data.pending}</div>
                                    <div className="text-xs text-cream/60 mt-1 font-outfit">vest when your next clean is complete</div>
                                </div>
                                <div>
                                    <div className="uppercase tracking-[0.2em] text-[10px] text-gold font-semibold mb-2">Lifetime</div>
                                    <div className="font-playfair text-3xl md:text-4xl text-cream">{data.lifetime_earned}</div>
                                    <div className="text-xs text-cream/60 mt-1 font-outfit">points ever earned</div>
                                </div>
                            </div>

                            {/* Existing vouchers */}
                            {data.vouchers?.length > 0 && (
                                <div className="mb-12" data-testid="active-vouchers">
                                    <h2 className="font-playfair text-2xl text-navy mb-4">Your active vouchers</h2>
                                    <div className="space-y-3">
                                        {data.vouchers.map((v) => (
                                            <div key={v.code} className="bg-white border border-gold p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                <div>
                                                    <div className="uppercase tracking-[0.15em] text-[10px] text-gold-dark font-semibold">{v.reward_label}</div>
                                                    <div className="font-mono tracking-[0.25em] text-lg text-navy mt-1" data-testid={`voucher-${v.code}`}>{v.code}</div>
                                                    <div className="text-xs text-muted-foreground mt-1 font-outfit">Worth €{v.discount_eur.toFixed(2)} — paste in the booking form</div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => copy(v.code)}
                                                        data-testid={`copy-voucher-${v.code}`}
                                                        className="inline-flex items-center gap-2 border border-navy text-navy px-4 py-2 font-outfit text-xs uppercase tracking-wider hover:bg-navy hover:text-gold transition-colors"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" /> Copy
                                                    </button>
                                                    <Link
                                                        to={`/book?voucher=${v.code}`}
                                                        className="inline-flex items-center gap-2 bg-navy text-gold px-4 py-2 font-outfit text-xs uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors"
                                                    >
                                                        Book now <ArrowRight className="w-3.5 h-3.5" />
                                                    </Link>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Rewards catalog */}
                            <div>
                                <h2 className="font-playfair text-2xl text-navy mb-6 flex items-center gap-2"><Trophy className="w-5 h-5 text-gold-dark" /> Redeem your points</h2>
                                <div className="grid md:grid-cols-3 gap-5">
                                    {data.rewards.map((r) => {
                                        const affordable = data.balance >= r.points;
                                        const progress = Math.min(100, Math.round((data.balance / r.points) * 100));
                                        return (
                                            <div key={r.key} data-testid={`reward-${r.key}`}
                                                className={`bg-white border p-6 flex flex-col ${affordable ? "border-gold" : "border-cream-border"}`}>
                                                <Gift className={`w-6 h-6 mb-3 ${affordable ? "text-gold-dark" : "text-muted-foreground"}`} />
                                                <div className="font-playfair text-xl text-navy">{r.label}</div>
                                                <div className="font-outfit text-sm text-muted-foreground mt-2 mb-4 flex-1">{r.description}</div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="uppercase tracking-[0.15em] text-[10px] text-navy font-semibold">Cost</span>
                                                    <span className="font-playfair text-lg text-gold-dark">{r.points} pts</span>
                                                </div>
                                                <div className="w-full h-1 bg-cream-border mb-3">
                                                    <div className="h-1 bg-gold transition-all" style={{ width: `${progress}%` }} />
                                                </div>
                                                <button
                                                    data-testid={`redeem-${r.key}`}
                                                    disabled={!affordable || redeeming === r.key}
                                                    onClick={() => redeem(r)}
                                                    className={`w-full py-3 font-outfit text-xs uppercase tracking-wider transition-colors ${
                                                        affordable
                                                            ? "bg-navy text-gold border border-gold hover:bg-gold hover:text-navy"
                                                            : "bg-cream border border-cream-border text-muted-foreground cursor-not-allowed"
                                                    }`}
                                                >
                                                    {redeeming === r.key ? "Redeeming…" : affordable ? "Redeem now" : `${r.points - data.balance} pts to go`}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* History */}
                            {data.history?.length > 0 && (
                                <div className="mt-14" data-testid="rewards-history">
                                    <h3 className="font-playfair text-xl text-navy mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold-dark" /> Recent activity</h3>
                                    <ul className="divide-y divide-cream-border bg-white border border-cream-border">
                                        {data.history.map((h, i) => (
                                            <li key={i} className="flex justify-between px-5 py-3 font-outfit text-sm">
                                                <span className="text-navy">
                                                    {h.kind === "earned"
                                                        ? `Booking earned (${h.status})`
                                                        : `Redeemed — ${h.reward_label || "reward"}`}
                                                </span>
                                                <span className={h.points >= 0 ? "text-gold-dark" : "text-red-500"}>
                                                    {h.points >= 0 ? "+" : ""}{h.points} pts
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}

                    {!data && !loading && (
                        <p className="text-center font-outfit text-sm text-muted-foreground mt-10">
                            Enter the email you used for your bookings above to see your points.
                        </p>
                    )}
                </div>
            </section>
            <Footer />
        </div>
    );
}
